import { apiFetch, apiFetchJson } from '@wundo/client/api.ts';

export type IntegrationProvider = 'microsoft' | 'google';

export const INTEGRATION_PROVIDERS: readonly IntegrationProvider[] = [
  'microsoft',
  'google',
];

/*
 * Calendar scopes are asked for here, at link time, and never in the provider
 * config. better-auth appends request scopes to the provider's own, so putting
 * them in the config would make *signing in* ask for calendar access — a
 * consent screen demanding someone's calendar before they have seen the
 * product. Linking is the moment the ask makes sense.
 */
const CALENDAR_SCOPES: Record<IntegrationProvider, string[]> = {
  microsoft: ['Calendars.Read'],
  google: ['https://www.googleapis.com/auth/calendar.events.readonly'],
};

/** How far the first read of a connected calendar has come. */
export type CalendarSyncStatus = 'pending' | 'ok' | 'error';

export interface IntegrationStatus {
  provider: IntegrationProvider;
  connected: boolean;
  /** The account row holding the calendar grant; null while not connected. */
  account_id: string | null;
  /** Null while the provider is not connected: no calendar, no sync to speak of. */
  sync_status: CalendarSyncStatus | null;
}

/** Each provider's connection as the server sees it. */
export async function fetchIntegrations(): Promise<IntegrationStatus[]> {
  const { integrations } = await apiFetchJson<{
    integrations: IntegrationStatus[];
  }>('/integrations');

  return integrations;
}

/**
 * Which calendars are actually connected.
 *
 * Answered from the calendar connections, not from which provider accounts
 * exist. Signing in with Microsoft creates an account, so reading connectedness
 * from accounts reports a calendar nobody granted — and then hides the button
 * that would grant it.
 */
export async function fetchConnectedProviders(): Promise<
  Record<IntegrationProvider, boolean>
> {
  const integrations = await fetchIntegrations();

  const connected = (provider: IntegrationProvider) =>
    integrations.some((entry) => entry.provider === provider && entry.connected);

  return { microsoft: connected('microsoft'), google: connected('google') };
}

/** Whether Wundo can read the calendar right now, as opposed to having once been granted it. */
export async function testConnection(provider: IntegrationProvider): Promise<boolean> {
  const { ok } = await apiFetchJson<{ ok: boolean }>(
    `/integrations/${provider}/actions/test`,
    { method: 'POST' },
  );

  return ok;
}

/*
 * Come back to this page, wherever it is being served from.
 *
 * Naming the route outright is what stranded people in the staff console: a
 * customer sent to the other app's path is turned away by its guard and lands
 * on a login screen, having just connected their calendar.
 */
function returnHere(): string {
  return `${window.location.origin}${window.location.pathname}`;
}

/**
 * Hand off to the provider's consent screen.
 *
 * The same call serves first-time connecting and reconnecting — both re-run
 * consent, which is the whole point of reconnecting.
 *
 * The redirect happens here rather than being handed back to the caller. A page
 * that receives a URL has to remember to navigate to it, and one that forgets
 * looks like a button that does nothing; there is also no rendering left to do
 * once the browser leaves.
 *
 * `returnTo` is where the provider sends the browser afterwards. It defaults to
 * the page that asked, which is what a settings row wants — it has nothing left
 * to do once the calendar is there. A flow that carries on somewhere else names
 * that place instead, and must name an address on this origin for the same
 * reason `returnHere` never names a route outright.
 *
 * A refusal goes back to the page that asked, always — never to `returnTo`,
 * which is the address for a calendar that now exists. Left unset the backend
 * falls back to its own error page, which is served by the staff console: a
 * customer who declined a consent screen, or whose administrator has to grant
 * it first, ended up on another app's error page with no way back. The reason
 * arrives on that page as `?error=`, the same way a failed sign-in carries one.
 */
export async function startCalendarLink(
  provider: IntegrationProvider,
  returnTo: string = returnHere(),
): Promise<void> {
  if (provider === 'microsoft') {
    const proxied = await proxyLinkUrl(returnTo);
    if (proxied !== null) {
      window.location.href = proxied;
      return;
    }
  }

  const { url } = await apiFetchJson<{ url: string }>('/auth/link-social', {
    method: 'POST',
    body: JSON.stringify({
      provider,
      scopes: CALENDAR_SCOPES[provider],
      callbackURL: returnTo,
      errorCallbackURL: returnHere(),
    }),
  });

  window.location.href = url;
}

/*
 * An organisation that reads its Microsoft calendars through its own
 * agendaproxy has people consent to its own app registration instead of
 * Wundo's. Only the server knows whether that is the case; null means not.
 */
async function proxyLinkUrl(returnTo: string): Promise<string | null> {
  const { url } = await apiFetchJson<{ url: string | null }>(
    '/integrations/microsoft/actions/proxy-link',
    {
      method: 'POST',
      body: JSON.stringify({
        callback_url: returnTo,
        error_callback_url: returnHere(),
      }),
    },
  );

  return url;
}

/**
 * Hand the calendar back.
 *
 * This unlinks the provider account, which for someone who signs in with that
 * provider is also how they sign in. That is deliberate and safe: `BetterAuth`
 * turns off better-auth's last-account guard because a magic link signs anyone
 * in without an account at all, and signing in with the provider again re-links
 * it.
 */
export async function disconnectCalendar(accountId: string): Promise<void> {
  await apiFetch('/auth/unlink-account', {
    method: 'POST',
    body: JSON.stringify({ accountId }),
  });
}
